import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ApiService from '../services/ApiService';

const UpdateRequest = () => {
    const { requestId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const checkRequestState = async () => {
            try {
                setLoading(true);

                console.log('🔍 Checking request state for ID:', requestId);

                if (!requestId || requestId === 'undefined') {
                    setError('Invalid request ID provided.');
                    setLoading(false);
                    return;
                }

                let requestData;
                let requestType = null; // 'agn_firewall', 'agn_webex', 'agn_ipt', or 'bandwidth'

                // 🔧 FIX: Checking URL for type parameter to avoid ID collisions
                const searchParams = new URLSearchParams(location.search);
                const typeParam = searchParams.get('type');

                if (typeParam) {
                    requestType = typeParam;

                    let endpoint;
                    if (typeParam === 'agn_firewall') {
                        endpoint = `/api/agn-firewall/${requestId}`;
                    } else if (typeParam === 'agn_webex') {
                        endpoint = `/api/webex-decommissioning/${requestId}`;
                    } else if (typeParam === 'agn_ipt') {
                        endpoint = `/api/ipt-decommissioning/${requestId}`;
                    } else if (typeParam === 'bandwidth') {
                        endpoint = `/api/requests/${requestId}`;
                    } else if (typeParam === 'circuit_order') {
                        endpoint = `/api/circuit-orders/${requestId}`;
                    } else if (typeParam === 'new_site') {
                        endpoint = `/api/new-sites/${requestId}`;
                    } else if (typeParam === 'site_expansion') {
                        endpoint = `/api/site-expansion/${requestId}`;
                    } else {
                        setError(`Unknown request type: ${typeParam}`);
                        setLoading(false);
                        return;
                    }
                    requestData = await ApiService.get(endpoint);
                } else {
                    // No type parameter - use discovery pattern (backward compatibility)
                    console.log('⚠️ No type parameter - using discovery pattern (may have ID collisions)');

                    try {
                        // Try AGN Firewall endpoint first
                        console.log('🟠 Trying AGN Firewall endpoint...');
                        requestData = await ApiService.get(`/api/agn-firewall/${requestId}`);
                        requestType = 'agn_firewall';
                        console.log('✅ Found AGN Firewall request:', requestData);
                    } catch (agnError) {
                        console.log('❌ AGN Firewall not found, trying Webex Decommissioning endpoint...');
                        try {
                            requestData = await ApiService.get(`/api/webex-decommissioning/${requestId}`);
                            requestType = 'agn_webex';
                            console.log('✅ Found Webex Decommissioning request:', requestData);
                        } catch (webexError) {
                            console.log('❌ Webex not found, trying IPT Decommissioning endpoint...');
                            try {
                                requestData = await ApiService.get(`/api/ipt-decommissioning/${requestId}`);
                                requestType = 'agn_ipt';
                                console.log('✅ Found IPT Decommissioning request:', requestData);
                            } catch (iptError) {
                                console.log('❌ IPT not found, trying Bandwidth endpoint...');
                                try {
                                    requestData = await ApiService.get(`/api/requests/${requestId}`);
                                    requestType = 'bandwidth';
                                    console.log('✅ Found Bandwidth request:', requestData);
                                } catch (bandwidthError) {
                                    console.error('❌ Request not found in any endpoint:', bandwidthError);
                                    setError(`Request not found: ${bandwidthError.message}`);
                                    setLoading(false);
                                    return;
                                }
                            }
                        }
                    }
                }

                // Get current state (handles both camelCase and snake_case)
                const currentState = requestData.requestState ||
                                    requestData.request_state ||
                                    'Unknown';

                console.log(`📋 ${requestType} request state:`, currentState);

                // 🔧 KEY FIX: Only redirect if state is Draft
                if (currentState === 'Draft') {
                    console.log(`✅ ${requestType} request in Draft, allowing edit`);

                    // Route to appropriate form based on request type
                    if (requestType === 'agn_firewall') {
                        navigate(`/new-request?updateId=${requestId}&type=agn_firewall`);
                    } else if (requestType === 'agn_webex') {
                        navigate(`/new-request?updateId=${requestId}&type=agn_webex`);
                    } else if (requestType === 'agn_ipt') {
                        navigate(`/new-request?updateId=${requestId}&type=agn_ipt`);
                    } else if (requestType === 'circuit_order') {
                        navigate(`/new-request?updateId=${requestId}&type=circuit_order`);
                    } else if (requestType === 'new_site') {
                        navigate(`/new-request?updateId=${requestId}&type=new_site`);
                    } else if (requestType === 'site_expansion') {
                        navigate(`/new-request?updateId=${requestId}&type=site_expansion`);
                    } else {
                        // Bandwidth
                        navigate(`/new-request?updateId=${requestId}`);
                    }
                } else {
                    // Not in Draft state - show error
                    console.log(`❌ ${requestType} request not in Draft state:`, currentState);
                    setLoading(false);
                    setError(`Only draft requests can be fully edited. Current state: ${currentState}`);
                }
            } catch (err) {
                console.error('❌ Error fetching request:', err);
                setError(`Failed to load request details: ${err.message}`);
                setLoading(false);
            }
        };

        checkRequestState();
    }, [requestId, navigate, location.search]);

    if (loading) {
        return (
            <Layout>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
            <Box mt={4} textAlign="center">
                <Typography variant="h5" color="error" gutterBottom>
                Update Not Allowed
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                {error}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                Only draft requests can be fully edited. Please go back to Track Requests page.
                </Typography>
                <Box mt={2}>
                <button
                    onClick={() => navigate('/track-requests')}
                    style={{
                        padding: '8px 16px',
                        background: '#003781',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    Back to Track Requests
                </button>
                </Box>
            </Box>
            </Layout>
        );
    }

    return null; // We shouldn't reach here as we'll redirect
};

export default UpdateRequest;
